import type { SearchResultItem } from "@/app/services/generated/searchService";
import {
  SearchQuery,
  SearchService,
} from "@/app/services/generated/searchService";
import messages, { translate, keys } from "@/plugins/i18n";
import type { RawLocation } from "vue-router";
import { routes } from "@/app/ui/navigation/menuItems";
import { contactsMenu } from "@/app/ui/navigation/contactsMenu";
import { EnumBmEntityType } from "../generated/enums/enumBmEntityType.generated";
import { ullageRoutes } from "@/app/ui/navigation/ullagesMenu";
import { priceListSubRoutes } from "@/app/ui/navigation/pricingMenu";
import { signInWithQuery } from "@/app/ui/navigation/accountManagement";
import { salesMenu } from "@/app/ui/navigation/salesMenu";
import { adminRoutes } from "@/app/ui/navigation/administrationMenu";
import { isNotNullOrUndefined, toMap } from "@/app/apiutils";

export type IResolvedSearchResult = {
  typeName: string;
  description: string;
  navigationRoute: RawLocation;
  isHidden: boolean;
};

const resolver = (
  type: string,
  typeName: string,
  resolveLocation: (item: SearchResultItem) => RawLocation
) => ({
  type,
  resolve: (searchResult: SearchResultItem): IResolvedSearchResult => ({
    typeName,
    description: searchResult.description,
    navigationRoute: resolveLocation(searchResult),
    isHidden: searchResult.isHidden,
  }),
});

const searchResolvers = toMap(
  [
    resolver("Outlet", EnumBmEntityType.outlet.translation, (result) =>
      contactsMenu.viewOutlet(result.objectId)
    ),
    resolver("OutletContact", messages.search_type_outlet_contact, (result) => {
      return {
        name: routes.contactsMenu.outlets.contacts,
        params: { id: result.objectId.toString() },
      };
    }),
    resolver("Group", EnumBmEntityType.outletGroup.translation, (result) => {
      return {
        name: routes.contactsMenu.outletGroups.view,
        params: { id: result.objectId.toString() },
      };
    }),
    resolver("GroupContact", messages.search_type_group_contact, (result) => {
      return {
        name: routes.contactsMenu.outletGroups.contacts,
        params: { id: result.objectId.toString() },
      };
    }),
    resolver("Order", EnumBmEntityType.order.translation, (result) =>
      routes.salesMenu.viewOrder(result.objectId)
    ),
    resolver("CreditNote", EnumBmEntityType.credit.translation, (result) =>
      salesMenu.viewCreditNote(result.objectId)
    ),
    resolver("Material", EnumBmEntityType.material.translation, (result) => {
      return {
        name: routes.stockMenu.stock.view,
        params: { id: result.objectId.toString() },
      };
    }),
    resolver("Product", EnumBmEntityType.product.translation, (result) => {
      return {
        name: routes.stockMenu.stock.view,
        params: { id: result.objectId.toString() },
      };
    }),
    resolver(
      "AutoAssembledItem",
      messages.search_type_auto_assembled_item,
      (result) => {
        return {
          name: routes.stockMenu.stock.view,
          params: { id: result.objectId.toString() },
        };
      }
    ),
    resolver(
      "DeliveryService",
      messages.distribution_delivery_service_noun,
      (result) => {
        return {
          name: routes.distributionMenu.settings.deliveryServices.edit,
          params: { id: result.objectId.toString(), mode: "view" },
        };
      }
    ),
    resolver(
      "BottleDepositCharge",
      messages.bottle_deposit_charge_item_noun,
      (result) =>
        adminRoutes.editBottleDepositChargeItem.location({
          id: result.objectId,
          editing: false,
        })
    ),
    resolver(
      "BottleDepositRedemption",
      messages.bottle_deposit_redemption_item_noun,
      (result) =>
        adminRoutes.editBottleDepositRedemptionItem.location({
          id: result.objectId,
          editing: false,
        })
    ),
    resolver("MaterialProduction", messages.search_type_recipe, (result) => {
      return {
        name: routes.productionMenu.recipe.view,
        params: { id: result.objectId.toString() },
      };
    }),
    resolver("ProductPackaging", messages.search_type_packaging, (result) => {
      return {
        name: routes.productionMenu.packaging.view,
        params: { id: result.objectId.toString() },
      };
    }),
    resolver("Assembly", EnumBmEntityType.assembly.translation, (result) => {
      return {
        name: routes.productionMenu.assembly.view,
        params: { id: result.objectId.toString() },
      };
    }),
    resolver("PriceList", EnumBmEntityType.priceList.translation, (result) =>
      priceListSubRoutes.pricesTab.location(result.objectId)
    ),
    resolver("Supplier", EnumBmEntityType.supplier.translation, (result) =>
      routes.contactsMenu.viewSupplier(result.objectId)
    ),
    resolver(
      "PurchaseOrder",
      EnumBmEntityType.purchaseOrder.translation,
      (result) =>
        routes.stockMenu.purchaseOrdering.viewPurchaseOrder(result.objectId)
    ),
    resolver("Ullage", EnumBmEntityType.ullage.translation, (result) =>
      ullageRoutes.view.location(result.objectId)
    ),
    resolver("Container", messages.search_type_container, (result) =>
      routes.containerTrackingMenu.viewContainer(result.objectId)
    ),
    resolver("Lead", translate(keys.leads_noun).toString(), (result) =>
      routes.salesMenu.leads.viewLead(result.objectId)
    ),
    resolver(
      "ProductBrand",
      translate(keys.product_brand).toString(),
      (result) =>
        routes.stockMenu.productBrands.editLocation(result.objectId, false)
    ),
    resolver("StockTake", translate(keys.stock_take).toString(), (result) =>
      routes.stockMenu.stockTake.viewLocation(result.objectId)
    ),
    resolver(
      "StockTransfer",
      translate(keys.stock_transfer_noun).toString(),
      (result) =>
        routes.stockMenu.stockMovements.stockTransfer.editStockTransfer(
          result.objectId,
          false
        )
    ),
    resolver(
      "StockAdjustment",
      translate(keys.stock_adjustment_noun).toString(),
      (result) =>
        routes.stockMenu.stockMovements.stockAdjustment.viewStockAdjustment(
          result.objectId,
          false
        )
    ),
    resolver("Pallet", translate(keys.pallet_noun).toString(), (result) =>
      routes.distributionMenu.pallets.viewLocation(result.objectId)
    ),
    resolver("TenantGroup", messages.enum_noun_tenant_group, (result) =>
      signInWithQuery({
        support: result.description,
        autoTenantGroupId: result.objectId.toString(),
      })
    ),
    resolver("PoDeliveryService", messages.po_delivery_service, (result) =>
      adminRoutes.editPoDeliveryService.location({
        id: result.objectId,
        editing: false,
      })
    ),
    resolver("Batch", EnumBmEntityType.batch.translation, (result) =>
      routes.batchMenu.batch.viewBatch(result.objectId)
    ),
  ],
  (x) => x.type
);

export default class SearchServiceExtended extends SearchService {
  public async typedSearch(
    searchQuery: string
  ): Promise<IResolvedSearchResult[]> {
    const query = new SearchQuery();
    query.query = searchQuery;

    return super.search(query).then((value) =>
      value.searchResultItems
        .map((v) => searchResolvers.get(v.type)?.resolve(v))
        .filter(isNotNullOrUndefined)
        .sort((a, b) => {
          if (a.isHidden === b.isHidden) {
            return 0;
          }
          if (a.isHidden) {
            return 1;
          }
          return -1;
        })
    );
  }
}

export const searchService = new SearchServiceExtended();
